import { useState, useEffect, useContext } from "react";
import { toast } from "react-hot-toast";
import { getRichTextLimitError } from "@/utils/richText";
import { RichTextEditor } from "../../../components/Editor/RichTextEditor";
import { ImageUploader } from "../../../components/ImageUploader/ImageUploader";
import { storageService } from "../../../services/storage.service";
import { supportService } from "../../../services/support.service";
import { AuthContext } from "@/contexts/AuthContext/context";
import { useBrandAuth } from "@/contexts/BrandAuthContext/context";
import { useAgencyAuth } from "@/contexts/AgencyAuthContext";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";

export function Support() {
  // Use a key to force re-render the entire component after submission
  const [formKey, setFormKey] = useState(0);
  const { user } = useContext(AuthContext);
  const { user: brandUser } = useBrandAuth();
  const { user: agencyUser } = useAgencyAuth();
  
  const [formData, setFormData] = useState({
    description: "",
    supportingMedia: [] as { file: File; preview: string }[],
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Function to reset the entire form
  const resetForm = () => {
    // Clean up preview URLs
    formData.supportingMedia.forEach((media) => {
      URL.revokeObjectURL(media.preview);
    });
    
    // Reset form data
    setFormData({
      description: "",
      supportingMedia: [],
    });
    
    // Force a complete re-render of the component
    setFormKey(prevKey => prevKey + 1);
  };

  const handleDescriptionChange = (content: string) => {
    setFormData({ ...formData, description: content });
  };

  const handleFileUpload = (file: File) => {
    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setFormData((prev) => ({
      ...prev,
      supportingMedia: [...prev.supportingMedia, { file, preview: previewUrl }],
    }));
  };

  const handleRemoveFile = (index: number) => {
    setFormData((prev) => {
      const newMedia = [...prev.supportingMedia];
      // Revoke the preview URL to avoid memory leaks
      URL.revokeObjectURL(newMedia[index].preview);
      newMedia.splice(index, 1);
      return { ...prev, supportingMedia: newMedia };
    });
  };

  // Clean up preview URLs when component unmounts
  useEffect(() => {
    return () => {
      formData.supportingMedia.forEach((media) => {
        URL.revokeObjectURL(media.preview);
      });
    };
  }, [formData.supportingMedia]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.description.trim()) {
      toast.error("Please provide a description of your issue");
      return;
    }

    const descriptionError = getRichTextLimitError(formData.description, 6000);
    if (descriptionError) {
      toast.error(descriptionError);
      return;
    }

    try {
      setIsSubmitting(true);

      // Upload all media files
      const mediaLinks = await Promise.all(
        formData.supportingMedia.map(async (media) => {
          return await storageService.uploadSupportMedia(media.file);
        })
      );

      // Create support ticket
      if (user?.role) {
      await supportService.createTicket({
          description: formData.description,
          mediaLinks,
        }, user.role.toLowerCase());
      }
      else if (brandUser?.role) {
        await supportService.createTicket({
          description: formData.description,
          mediaLinks,
        }, brandUser.role.toLowerCase());
      }
      else if (agencyUser?.role) {
        await supportService.createTicket({
          description: formData.description,
          mediaLinks,
        }, agencyUser.role.toLowerCase());
      }

      toast.success("Support ticket created successfully");

      // Use the resetForm function to completely reset the form
      resetForm();
    } catch (error) {
      console.error("Failed to submit support request:", error);
      toast.error("Failed to create support ticket");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <h1 className="text-2xl font-semibold tracking-tight text-foreground">
        Support
      </h1>

      <form id="support-form" key={formKey} onSubmit={handleSubmit} className="space-y-6">
        {/* Problem Description */}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-[200px,1fr] sm:gap-8 items-start">
          <label className="text-sm font-medium text-muted-foreground">
            Tell us your problem
          </label>
          <div>
            <RichTextEditor
              content={formData.description}
              onChange={handleDescriptionChange}
              placeholder="please provide as much detail as possible"
              maxLength={6000}
            />
          </div>
        </div>

        {/* Upload Media */}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-[200px,1fr] sm:gap-8 items-start">
          <label className="text-sm font-medium text-muted-foreground">
            Upload supporting media
          </label>
          <div>
            <ImageUploader
              helpText="formats: PNG, JPG or WebP (max. size 3MB)"
              image=""
              onChange={handleFileUpload}
              size="lg"
              shape="rectangular"
              id="support-media-upload"
            />
            {formData.supportingMedia.length > 0 && (
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {formData.supportingMedia.map((media, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={media.preview}
                      alt={`Supporting media ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(index)}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-[200px,1fr] sm:gap-8">
          <div className="hidden sm:block"></div>
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setFormData({
                  description: "",
                  supportingMedia: [],
                });
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Spinner size="sm" className="border-white" />
                  Submitting...
                </>
              ) : (
                "Submit problem"
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
